// UI strings for the plan-change confirm step (PlanChangeConfirm), shown to a
// web subscriber before their plan changes: the amount due now for an
// upgrade, or the date a downgrade takes effect. Merged into the i18n
// `planChange` namespace via registerPlanChangeI18n.ts. Languages missing
// here fall back to `en`. Rules behind it live in server/planSwitch.ts.

const planChangeTranslations = {
  en: {
    planName: "{{plan}} {{period}}",
    weekly: "Weekly",
    monthly: "Monthly",
    yearly: "Yearly",
    perWeek: "/week",
    perMonth: "/month",
    perYear: "/year",
    titleNow: "Switch to {{plan}} now?",
    titleLater: "Switch to {{plan}} on {{date}}?",
    titleScheduled: "You're switching to {{plan}} on {{date}}",
    newPlanLine: "{{plan}}, starting today",
    unusedLine: "Unused time on your current plan",
    accountCreditLine: "Account credit",
    adjustmentsLine: "Discounts and taxes",
    dueToday: "Due today",
    nowRenews: "Your plan changes now and renews on {{date}}.",
    nowCredits: "{{credits}} credits are added to your balance now.",
    laterNote:
      "You keep your current plan and credits until then. From {{date}} you pay {{price}}{{per}}. Nothing is charged today.",
    scheduledNote:
      "You keep your current plan until then. Nothing has been charged.",
    replacesNote: "This replaces your scheduled switch to {{plan}} on {{date}}.",
    cancel: "Cancel",
    close: "Close",
    confirmPay: "Pay {{amount}} and switch",
    confirmNow: "Switch now",
    confirmLater: "Schedule switch",
    keepCurrent: "Keep my current plan",
    toastSwitched: "You're now on {{plan}}.",
    toastScheduled: "Your plan changes to {{plan}} on {{date}}.",
    toastKept: "You're staying on your current plan.",
    alreadyOnPlan: "You're already on this plan.",
  },
  es: {
    planName: "{{plan}} {{period}}",
    weekly: "Semanal",
    monthly: "Mensual",
    yearly: "Anual",
    perWeek: "/semana",
    perMonth: "/mes",
    perYear: "/año",
    titleNow: "¿Cambiar a {{plan}} ahora?",
    titleLater: "¿Cambiar a {{plan}} el {{date}}?",
    titleScheduled: "Cambiarás a {{plan}} el {{date}}",
    newPlanLine: "{{plan}}, desde hoy",
    unusedLine: "Tiempo no usado de tu plan actual",
    accountCreditLine: "Saldo a favor",
    adjustmentsLine: "Descuentos e impuestos",
    dueToday: "A pagar hoy",
    nowRenews: "Tu plan cambia ahora y se renueva el {{date}}.",
    nowCredits: "Se añaden {{credits}} créditos a tu saldo ahora.",
    laterNote:
      "Mantienes tu plan actual y tus créditos hasta entonces. Desde el {{date}} pagarás {{price}}{{per}}. Hoy no se cobra nada.",
    scheduledNote:
      "Mantienes tu plan actual hasta entonces. No se ha cobrado nada.",
    replacesNote:
      "Esto reemplaza tu cambio programado a {{plan}} del {{date}}.",
    cancel: "Cancelar",
    close: "Cerrar",
    confirmPay: "Pagar {{amount}} y cambiar",
    confirmNow: "Cambiar ahora",
    confirmLater: "Programar cambio",
    keepCurrent: "Mantener mi plan actual",
    toastSwitched: "Ahora tienes el plan {{plan}}.",
    toastScheduled: "Tu plan cambiará a {{plan}} el {{date}}.",
    toastKept: "Te quedas con tu plan actual.",
    alreadyOnPlan: "Ya tienes este plan.",
  },
  ja: {
    planName: "{{plan}}（{{period}}）",
    weekly: "週払い",
    monthly: "月払い",
    yearly: "年払い",
    perWeek: "/週",
    perMonth: "/月",
    perYear: "/年",
    titleNow: "今すぐ{{plan}}に変更しますか？",
    titleLater: "{{date}}に{{plan}}へ変更しますか？",
    titleScheduled: "{{date}}に{{plan}}へ変更予定です",
    newPlanLine: "{{plan}}（本日開始）",
    unusedLine: "現在のプランの未使用期間",
    accountCreditLine: "アカウント残高",
    adjustmentsLine: "割引・税",
    dueToday: "本日のお支払い",
    nowRenews: "プランは今すぐ変更され、{{date}}に更新されます。",
    nowCredits: "{{credits}}クレジットが今すぐ残高に追加されます。",
    laterNote:
      "それまでは現在のプランとクレジットをそのままご利用いただけます。{{date}}から{{price}}{{per}}のお支払いとなります。本日の請求はありません。",
    scheduledNote:
      "それまでは現在のプランをご利用いただけます。請求は発生していません。",
    replacesNote:
      "{{date}}に予定されていた{{plan}}への変更は、この変更に置き換わります。",
    cancel: "キャンセル",
    close: "閉じる",
    confirmPay: "{{amount}}を支払って変更",
    confirmNow: "今すぐ変更",
    confirmLater: "変更を予約",
    keepCurrent: "現在のプランを継続",
    toastSwitched: "{{plan}}に変更しました。",
    toastScheduled: "{{date}}に{{plan}}へ変更されます。",
    toastKept: "現在のプランを継続します。",
    alreadyOnPlan: "すでにこのプランをご利用中です。",
  },
  pt: {
    planName: "{{plan}} {{period}}",
    weekly: "Semanal",
    monthly: "Mensal",
    yearly: "Anual",
    perWeek: "/semana",
    perMonth: "/mês",
    perYear: "/ano",
    titleNow: "Mudar para {{plan}} agora?",
    titleLater: "Mudar para {{plan}} em {{date}}?",
    titleScheduled: "Você vai mudar para {{plan}} em {{date}}",
    newPlanLine: "{{plan}}, a partir de hoje",
    unusedLine: "Tempo não usado do plano atual",
    accountCreditLine: "Crédito na conta",
    adjustmentsLine: "Descontos e impostos",
    dueToday: "A pagar hoje",
    nowRenews: "Seu plano muda agora e renova em {{date}}.",
    nowCredits: "{{credits}} créditos são adicionados ao seu saldo agora.",
    laterNote:
      "Você mantém seu plano atual e seus créditos até lá. A partir de {{date}} você paga {{price}}{{per}}. Nada é cobrado hoje.",
    scheduledNote: "Você mantém seu plano atual até lá. Nada foi cobrado.",
    replacesNote:
      "Isso substitui sua mudança agendada para {{plan}} em {{date}}.",
    cancel: "Cancelar",
    close: "Fechar",
    confirmPay: "Pagar {{amount}} e mudar",
    confirmNow: "Mudar agora",
    confirmLater: "Agendar mudança",
    keepCurrent: "Manter meu plano atual",
    toastSwitched: "Agora você está no {{plan}}.",
    toastScheduled: "Seu plano muda para {{plan}} em {{date}}.",
    toastKept: "Você continua no seu plano atual.",
    alreadyOnPlan: "Você já está neste plano.",
  },
  fr: {
    planName: "{{plan}} {{period}}",
    weekly: "Hebdomadaire",
    monthly: "Mensuel",
    yearly: "Annuel",
    perWeek: "/semaine",
    perMonth: "/mois",
    perYear: "/an",
    titleNow: "Passer à {{plan}} maintenant ?",
    titleLater: "Passer à {{plan}} le {{date}} ?",
    titleScheduled: "Vous passerez à {{plan}} le {{date}}",
    newPlanLine: "{{plan}}, à partir d'aujourd'hui",
    unusedLine: "Temps non utilisé de votre forfait actuel",
    accountCreditLine: "Crédit du compte",
    adjustmentsLine: "Réductions et taxes",
    dueToday: "À payer aujourd'hui",
    nowRenews: "Votre forfait change maintenant et se renouvelle le {{date}}.",
    nowCredits: "{{credits}} crédits sont ajoutés à votre solde maintenant.",
    laterNote:
      "Vous gardez votre forfait actuel et vos crédits jusque-là. À partir du {{date}}, vous payez {{price}}{{per}}. Rien n'est facturé aujourd'hui.",
    scheduledNote:
      "Vous gardez votre forfait actuel jusque-là. Rien n'a été facturé.",
    replacesNote: "Cela remplace votre passage prévu à {{plan}} le {{date}}.",
    cancel: "Annuler",
    close: "Fermer",
    confirmPay: "Payer {{amount}} et changer",
    confirmNow: "Changer maintenant",
    confirmLater: "Programmer le changement",
    keepCurrent: "Garder mon forfait actuel",
    toastSwitched: "Vous êtes maintenant sur {{plan}}.",
    toastScheduled: "Votre forfait passera à {{plan}} le {{date}}.",
    toastKept: "Vous gardez votre forfait actuel.",
    alreadyOnPlan: "Vous avez déjà ce forfait.",
  },
  zh: {
    planName: "{{plan}}（{{period}}）",
    weekly: "按周",
    monthly: "按月",
    yearly: "按年",
    perWeek: "/周",
    perMonth: "/月",
    perYear: "/年",
    titleNow: "现在切换到 {{plan}}？",
    titleLater: "于 {{date}} 切换到 {{plan}}？",
    titleScheduled: "将于 {{date}} 切换到 {{plan}}",
    newPlanLine: "{{plan}}，从今天开始",
    unusedLine: "当前套餐未使用的时间",
    accountCreditLine: "账户余额",
    adjustmentsLine: "折扣和税费",
    dueToday: "今日应付",
    nowRenews: "套餐立即变更，并于 {{date}} 续订。",
    nowCredits: "{{credits}} 积分将立即加入你的余额。",
    laterNote:
      "在此之前，你保留当前套餐和积分。从 {{date}} 起按 {{price}}{{per}} 收费。今天不会扣款。",
    scheduledNote: "在此之前，你保留当前套餐。目前没有扣款。",
    replacesNote: "这将替换你原定于 {{date}} 切换到 {{plan}} 的安排。",
    cancel: "取消",
    close: "关闭",
    confirmPay: "支付 {{amount}} 并切换",
    confirmNow: "立即切换",
    confirmLater: "预约切换",
    keepCurrent: "保留当前套餐",
    toastSwitched: "你现在使用的是 {{plan}}。",
    toastScheduled: "你的套餐将于 {{date}} 变更为 {{plan}}。",
    toastKept: "你将继续使用当前套餐。",
    alreadyOnPlan: "你已经在使用此套餐。",
  },
  ko: {
    planName: "{{plan}} ({{period}})",
    weekly: "주간",
    monthly: "월간",
    yearly: "연간",
    perWeek: "/주",
    perMonth: "/월",
    perYear: "/년",
    titleNow: "지금 {{plan}}(으)로 변경할까요?",
    titleLater: "{{date}}에 {{plan}}(으)로 변경할까요?",
    titleScheduled: "{{date}}에 {{plan}}(으)로 변경될 예정입니다",
    newPlanLine: "{{plan}}, 오늘부터",
    unusedLine: "현재 플랜의 미사용 기간",
    accountCreditLine: "계정 잔액",
    adjustmentsLine: "할인 및 세금",
    dueToday: "오늘 결제 금액",
    nowRenews: "플랜이 지금 변경되며 {{date}}에 갱신됩니다.",
    nowCredits: "{{credits}} 크레딧이 지금 잔액에 추가됩니다.",
    laterNote:
      "그때까지 현재 플랜과 크레딧을 그대로 사용할 수 있습니다. {{date}}부터 {{price}}{{per}}이(가) 청구됩니다. 오늘은 청구되지 않습니다.",
    scheduledNote:
      "그때까지 현재 플랜을 그대로 사용할 수 있습니다. 청구된 금액은 없습니다.",
    replacesNote: "{{date}}에 예정된 {{plan}}(으)로의 변경을 대체합니다.",
    cancel: "취소",
    close: "닫기",
    confirmPay: "{{amount}} 결제하고 변경",
    confirmNow: "지금 변경",
    confirmLater: "변경 예약",
    keepCurrent: "현재 플랜 유지",
    toastSwitched: "이제 {{plan}}을(를) 이용 중입니다.",
    toastScheduled: "{{date}}에 플랜이 {{plan}}(으)로 변경됩니다.",
    toastKept: "현재 플랜을 계속 이용합니다.",
    alreadyOnPlan: "이미 이 플랜을 이용 중입니다.",
  },
  id: {
    planName: "{{plan}} {{period}}",
    weekly: "Mingguan",
    monthly: "Bulanan",
    yearly: "Tahunan",
    perWeek: "/minggu",
    perMonth: "/bulan",
    perYear: "/tahun",
    titleNow: "Beralih ke {{plan}} sekarang?",
    titleLater: "Beralih ke {{plan}} pada {{date}}?",
    titleScheduled: "Kamu akan beralih ke {{plan}} pada {{date}}",
    newPlanLine: "{{plan}}, mulai hari ini",
    unusedLine: "Sisa waktu paket saat ini",
    accountCreditLine: "Saldo akun",
    adjustmentsLine: "Diskon dan pajak",
    dueToday: "Dibayar hari ini",
    nowRenews: "Paketmu berubah sekarang dan diperpanjang pada {{date}}.",
    nowCredits: "{{credits}} kredit ditambahkan ke saldomu sekarang.",
    laterNote:
      "Kamu tetap memakai paket dan kredit saat ini sampai saat itu. Mulai {{date}} kamu membayar {{price}}{{per}}. Tidak ada tagihan hari ini.",
    scheduledNote:
      "Kamu tetap memakai paket saat ini sampai saat itu. Belum ada tagihan.",
    replacesNote:
      "Ini menggantikan peralihan terjadwal ke {{plan}} pada {{date}}.",
    cancel: "Batal",
    close: "Tutup",
    confirmPay: "Bayar {{amount}} dan beralih",
    confirmNow: "Beralih sekarang",
    confirmLater: "Jadwalkan peralihan",
    keepCurrent: "Tetap di paket saat ini",
    toastSwitched: "Sekarang kamu memakai {{plan}}.",
    toastScheduled: "Paketmu berubah ke {{plan}} pada {{date}}.",
    toastKept: "Kamu tetap di paket saat ini.",
    alreadyOnPlan: "Kamu sudah memakai paket ini.",
  },
  it: {
    planName: "{{plan}} {{period}}",
    weekly: "Settimanale",
    monthly: "Mensile",
    yearly: "Annuale",
    perWeek: "/settimana",
    perMonth: "/mese",
    perYear: "/anno",
    titleNow: "Passare a {{plan}} ora?",
    titleLater: "Passare a {{plan}} il {{date}}?",
    titleScheduled: "Passerai a {{plan}} il {{date}}",
    newPlanLine: "{{plan}}, da oggi",
    unusedLine: "Tempo non usato del piano attuale",
    accountCreditLine: "Credito sull'account",
    adjustmentsLine: "Sconti e tasse",
    dueToday: "Da pagare oggi",
    nowRenews: "Il tuo piano cambia ora e si rinnova il {{date}}.",
    nowCredits: "{{credits}} crediti vengono aggiunti al tuo saldo ora.",
    laterNote:
      "Fino ad allora mantieni il piano attuale e i tuoi crediti. Dal {{date}} paghi {{price}}{{per}}. Oggi non viene addebitato nulla.",
    scheduledNote:
      "Fino ad allora mantieni il piano attuale. Non è stato addebitato nulla.",
    replacesNote: "Sostituisce il passaggio programmato a {{plan}} del {{date}}.",
    cancel: "Annulla",
    close: "Chiudi",
    confirmPay: "Paga {{amount}} e cambia",
    confirmNow: "Cambia ora",
    confirmLater: "Programma il cambio",
    keepCurrent: "Mantieni il piano attuale",
    toastSwitched: "Ora hai il piano {{plan}}.",
    toastScheduled: "Il tuo piano passerà a {{plan}} il {{date}}.",
    toastKept: "Mantieni il tuo piano attuale.",
    alreadyOnPlan: "Hai già questo piano.",
  },
  nl: {
    planName: "{{plan}} {{period}}",
    weekly: "Wekelijks",
    monthly: "Maandelijks",
    yearly: "Jaarlijks",
    perWeek: "/week",
    perMonth: "/maand",
    perYear: "/jaar",
    titleNow: "Nu overstappen naar {{plan}}?",
    titleLater: "Op {{date}} overstappen naar {{plan}}?",
    titleScheduled: "Je stapt op {{date}} over naar {{plan}}",
    newPlanLine: "{{plan}}, vanaf vandaag",
    unusedLine: "Ongebruikte tijd van je huidige abonnement",
    accountCreditLine: "Tegoed op je account",
    adjustmentsLine: "Kortingen en belastingen",
    dueToday: "Vandaag te betalen",
    nowRenews: "Je abonnement verandert nu en wordt verlengd op {{date}}.",
    nowCredits: "Er worden nu {{credits}} credits aan je saldo toegevoegd.",
    laterNote:
      "Tot dan houd je je huidige abonnement en credits. Vanaf {{date}} betaal je {{price}}{{per}}. Vandaag wordt er niets afgeschreven.",
    scheduledNote:
      "Tot dan houd je je huidige abonnement. Er is niets afgeschreven.",
    replacesNote: "Dit vervangt je geplande overstap naar {{plan}} op {{date}}.",
    cancel: "Annuleren",
    close: "Sluiten",
    confirmPay: "{{amount}} betalen en overstappen",
    confirmNow: "Nu overstappen",
    confirmLater: "Overstap plannen",
    keepCurrent: "Huidig abonnement houden",
    toastSwitched: "Je hebt nu {{plan}}.",
    toastScheduled: "Je abonnement wordt op {{date}} {{plan}}.",
    toastKept: "Je houdt je huidige abonnement.",
    alreadyOnPlan: "Je hebt dit abonnement al.",
  },
  pl: {
    planName: "{{plan}} {{period}}",
    weekly: "Tygodniowy",
    monthly: "Miesięczny",
    yearly: "Roczny",
    perWeek: "/tydz.",
    perMonth: "/mies.",
    perYear: "/rok",
    titleNow: "Zmienić plan na {{plan}} teraz?",
    titleLater: "Zmienić plan na {{plan}} {{date}}?",
    titleScheduled: "Twój plan zmieni się na {{plan}} {{date}}",
    newPlanLine: "{{plan}}, od dziś",
    unusedLine: "Niewykorzystany czas obecnego planu",
    accountCreditLine: "Środki na koncie",
    adjustmentsLine: "Rabaty i podatki",
    dueToday: "Do zapłaty dziś",
    nowRenews: "Plan zmienia się teraz i odnowi się {{date}}.",
    nowCredits: "{{credits}} kredytów trafia teraz na Twoje saldo.",
    laterNote:
      "Do tego czasu zachowujesz obecny plan i kredyty. Od {{date}} płacisz {{price}}{{per}}. Dziś nic nie zostanie pobrane.",
    scheduledNote:
      "Do tego czasu zachowujesz obecny plan. Nic nie zostało pobrane.",
    replacesNote:
      "To zastępuje zaplanowaną zmianę na {{plan}} z dnia {{date}}.",
    cancel: "Anuluj",
    close: "Zamknij",
    confirmPay: "Zapłać {{amount}} i zmień",
    confirmNow: "Zmień teraz",
    confirmLater: "Zaplanuj zmianę",
    keepCurrent: "Zostaw obecny plan",
    toastSwitched: "Masz teraz plan {{plan}}.",
    toastScheduled: "Twój plan zmieni się na {{plan}} {{date}}.",
    toastKept: "Zostajesz przy obecnym planie.",
    alreadyOnPlan: "Masz już ten plan.",
  },
  tr: {
    planName: "{{plan}} {{period}}",
    weekly: "Haftalık",
    monthly: "Aylık",
    yearly: "Yıllık",
    perWeek: "/hafta",
    perMonth: "/ay",
    perYear: "/yıl",
    titleNow: "Şimdi {{plan}} planına geçilsin mi?",
    titleLater: "{{date}} tarihinde {{plan}} planına geçilsin mi?",
    titleScheduled: "{{date}} tarihinde {{plan}} planına geçeceksin",
    newPlanLine: "{{plan}}, bugünden itibaren",
    unusedLine: "Mevcut planının kullanılmayan süresi",
    accountCreditLine: "Hesap bakiyesi",
    adjustmentsLine: "İndirimler ve vergiler",
    dueToday: "Bugün ödenecek",
    nowRenews: "Planın şimdi değişir ve {{date}} tarihinde yenilenir.",
    nowCredits: "Bakiyene şimdi {{credits}} kredi eklenir.",
    laterNote:
      "O zamana kadar mevcut planını ve kredilerini korursun. {{date}} tarihinden itibaren {{price}}{{per}} ödersin. Bugün ücret alınmaz.",
    scheduledNote:
      "O zamana kadar mevcut planını korursun. Herhangi bir ücret alınmadı.",
    replacesNote:
      "Bu, {{date}} tarihinde {{plan}} planına planlanmış geçişinin yerini alır.",
    cancel: "İptal",
    close: "Kapat",
    confirmPay: "{{amount}} öde ve geç",
    confirmNow: "Şimdi geç",
    confirmLater: "Geçişi planla",
    keepCurrent: "Mevcut planımda kal",
    toastSwitched: "Artık {{plan}} planındasın.",
    toastScheduled: "Planın {{date}} tarihinde {{plan}} olarak değişecek.",
    toastKept: "Mevcut planında kalıyorsun.",
    alreadyOnPlan: "Zaten bu plandasın.",
  },
  vi: {
    planName: "{{plan}} {{period}}",
    weekly: "Hằng tuần",
    monthly: "Hằng tháng",
    yearly: "Hằng năm",
    perWeek: "/tuần",
    perMonth: "/tháng",
    perYear: "/năm",
    titleNow: "Chuyển sang {{plan}} ngay bây giờ?",
    titleLater: "Chuyển sang {{plan}} vào {{date}}?",
    titleScheduled: "Bạn sẽ chuyển sang {{plan}} vào {{date}}",
    newPlanLine: "{{plan}}, bắt đầu từ hôm nay",
    unusedLine: "Thời gian chưa dùng của gói hiện tại",
    accountCreditLine: "Số dư tài khoản",
    adjustmentsLine: "Giảm giá và thuế",
    dueToday: "Thanh toán hôm nay",
    nowRenews: "Gói của bạn thay đổi ngay và gia hạn vào {{date}}.",
    nowCredits: "{{credits}} tín dụng được cộng vào số dư của bạn ngay bây giờ.",
    laterNote:
      "Đến lúc đó bạn vẫn giữ gói và tín dụng hiện tại. Từ {{date}} bạn trả {{price}}{{per}}. Hôm nay không bị trừ tiền.",
    scheduledNote:
      "Đến lúc đó bạn vẫn giữ gói hiện tại. Chưa có khoản phí nào.",
    replacesNote: "Thay đổi này thay thế lịch chuyển sang {{plan}} vào {{date}}.",
    cancel: "Hủy",
    close: "Đóng",
    confirmPay: "Thanh toán {{amount}} và chuyển",
    confirmNow: "Chuyển ngay",
    confirmLater: "Lên lịch chuyển",
    keepCurrent: "Giữ gói hiện tại",
    toastSwitched: "Bạn đang dùng gói {{plan}}.",
    toastScheduled: "Gói của bạn sẽ đổi sang {{plan}} vào {{date}}.",
    toastKept: "Bạn tiếp tục dùng gói hiện tại.",
    alreadyOnPlan: "Bạn đang dùng gói này rồi.",
  },
  de: {
    planName: "{{plan}} {{period}}",
    weekly: "Wöchentlich",
    monthly: "Monatlich",
    yearly: "Jährlich",
    perWeek: "/Woche",
    perMonth: "/Monat",
    perYear: "/Jahr",
    titleNow: "Jetzt zu {{plan}} wechseln?",
    titleLater: "Am {{date}} zu {{plan}} wechseln?",
    titleScheduled: "Du wechselst am {{date}} zu {{plan}}",
    newPlanLine: "{{plan}}, ab heute",
    unusedLine: "Ungenutzte Zeit deines aktuellen Plans",
    accountCreditLine: "Guthaben",
    adjustmentsLine: "Rabatte und Steuern",
    dueToday: "Heute fällig",
    nowRenews:
      "Dein Plan ändert sich jetzt und verlängert sich am {{date}}.",
    nowCredits: "{{credits}} Credits werden dir jetzt gutgeschrieben.",
    laterNote:
      "Bis dahin behältst du deinen aktuellen Plan und deine Credits. Ab dem {{date}} zahlst du {{price}}{{per}}. Heute wird nichts abgebucht.",
    scheduledNote:
      "Bis dahin behältst du deinen aktuellen Plan. Es wurde nichts abgebucht.",
    replacesNote: "Das ersetzt deinen geplanten Wechsel zu {{plan}} am {{date}}.",
    cancel: "Abbrechen",
    close: "Schließen",
    confirmPay: "{{amount}} zahlen und wechseln",
    confirmNow: "Jetzt wechseln",
    confirmLater: "Wechsel planen",
    keepCurrent: "Aktuellen Plan behalten",
    toastSwitched: "Du hast jetzt {{plan}}.",
    toastScheduled: "Dein Plan wechselt am {{date}} zu {{plan}}.",
    toastKept: "Du behältst deinen aktuellen Plan.",
    alreadyOnPlan: "Du hast diesen Plan bereits.",
  },
} as const;

export default planChangeTranslations;
